import React from "react";
import { Head, Link, usePage, router } from "@inertiajs/react";
import styled from "styled-components";
import { GlobalStyle } from "../../constants/global-style";
import { apiPost } from "../../api";
import { brand_orange_deep, brand_danger, dark } from "../../constants/styles";
import AuthLayout from "../../auth/AuthLayout";
import FormField, { Control } from "../../auth/FormField";
import PasswordField from "../../auth/PasswordField";
import SubmitButton from "../../auth/SubmitButton";
import InlineAlert from "../../auth/InlineAlert";
import GoogleSignInButton from "../../auth/GoogleSignInButton";
import useAuthForm, { normalizeServerErrors } from "../../auth/useAuthForm";

// Standalone login page. Replaces the homepage Bootstrap modal when the
// `auth_rebrand_v1` FeatureToggle is ON. See plan: /home/mczal/.claude/plans/…
//
// Inertia props:
//   redirect_after_login        — forwarded through the Rails POST so
//                                 SessionsController#url_after_create sends
//                                 the user back to where they were heading
//   google_sign_in_proceed_to   — URL Google redirects back to; already
//                                 encodes redirect_after_login
//
// Auth conditions handled:
//   L1 unconfirmed   — inline warning banner + "Kirim ulang" action
//   L2 wrong creds   — inline form error under password
//   L3 success       — router.visit(location_url)
//   L4 locked        — inline "akun diblokir" banner
//   L5 admin + OTP   — location_url points at /authentications/otp; same visit

const Heading = styled.h1`
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0 0 8px;
  color: #111827;

  [data-theme="dark"] & {
    color: ${dark.text_primary};
  }
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.7;

  [data-theme="dark"] & {
    color: ${dark.text_secondary};
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  font-size: 13px;
`;

const RememberLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #374151;
  cursor: pointer;

  & input {
    accent-color: ${brand_orange_deep};
    width: 16px;
    height: 16px;
  }

  [data-theme="dark"] & {
    color: ${dark.text_secondary};
  }
`;

const ForgotLink = styled.a`
  color: ${brand_orange_deep};
  font-weight: 600;
  text-decoration: none;

  &:hover, &:focus-visible { text-decoration: underline; }

  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #9ca3af;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 8px 0;

  &::before, &::after {
    content: "";
    flex: 1 1 auto;
    height: 1px;
    background: #e5e7eb;
  }

  [data-theme="dark"] & {
    color: ${dark.text_muted};

    &::before, &::after { background: ${dark.border}; }
  }
`;

const BaseError = styled.div`
  color: ${brand_danger};
  font-size: 13px;
  font-weight: 500;
  margin-top: -4px;
`;

// Secondary Daftar CTA — rendered directly below the primary Masuk submit
// button. Styled as an outlined pill so it reads as a distinct action (not
// just a link), without competing with the gradient primary above it.
const DaftarButton = styled(Link)`
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  padding: 12px 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px solid ${brand_orange_deep};
  border-radius: 12px;
  background: transparent;
  color: ${brand_orange_deep};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
  transition: background 150ms ease, color 150ms ease, transform 120ms ease, box-shadow 150ms ease;

  &:hover {
    background: ${brand_orange_deep};
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(216, 89, 7, 0.28);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 3px;
  }

  [data-theme="dark"] & {
    border-color: #fdba74;
    color: #fdba74;

    &:hover {
      background: #fdba74;
      color: #1f2937;
    }
  }
`;

export default function Login() {
  const { redirect_after_login: redirectAfterLogin, google_sign_in_proceed_to: googleProceedTo } =
    usePage().props;

  const { fields, setField, errors, mergeErrors, submitting, setSubmitting, clearError } =
    useAuthForm({ email: "", password: "", remember: false });
  const [unconfirmed, setUnconfirmed] = React.useState(null);
  const [resendingConfirm, setResendingConfirm] = React.useState(false);
  const [resendNotice, setResendNotice] = React.useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setUnconfirmed(null);
    try {
      const { parsedResponse, statusCode } = await apiPost("/authentications/sessions", {
        body: {
          session: {
            email: fields.email,
            password: fields.password,
          },
          remember: fields.remember ? 1 : 0,
          redirect_after_login: redirectAfterLogin || undefined,
        },
      });

      if (parsedResponse?.confirmation) {
        setUnconfirmed({ user_id: parsedResponse.user_id, email: parsedResponse.email });
        return;
      }

      if (statusCode >= 200 && statusCode < 300 && parsedResponse?.location_url) {
        router.visit(parsedResponse.location_url);
        return;
      }

      // Error paths — 401 locked / invalid, or any unexpected shape.
      const code = parsedResponse?.code;
      if (code === "locked") {
        mergeErrors({ base: parsedResponse?.alert || "Akun diblokir." });
      } else {
        const serverErrors = normalizeServerErrors(parsedResponse?.errors);
        mergeErrors({
          ...serverErrors,
          base: serverErrors.base || parsedResponse?.alert || "Email atau password salah.",
        });
      }
    } catch (err) {
      if (typeof console !== "undefined") console.warn("[Login] submit failed", err);
      mergeErrors({ base: "Terjadi kesalahan. Silakan coba lagi." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!unconfirmed?.user_id || resendingConfirm) return;
    setResendingConfirm(true);
    setResendNotice(null);
    try {
      await apiPost(
        `/authentications/registrations/confirmations/send_confirmation?id=${unconfirmed.user_id}&type=email&redirect_after_login=${encodeURIComponent(redirectAfterLogin || "/")}`,
        { body: {} },
      );
      setResendNotice("Email konfirmasi terkirim. Silakan cek inbox Anda.");
    } catch (err) {
      if (typeof console !== "undefined") console.warn("[Login] resend failed", err);
      setResendNotice("Gagal mengirim email. Coba lagi sebentar.");
    } finally {
      setResendingConfirm(false);
    }
  };

  return (
    <>
      <Head>
        <title>Masuk — Taxsam Learning Center</title>
        <meta name="description" content="Masuk ke akun Taxsam Learning Center — pusat pelatihan sertifikasi pajak Brevet dan webinar pajak terlengkap di Indonesia." />
      </Head>
      <GlobalStyle />
      <AuthLayout
        imageSide="left"
        crossLink={{ prompt: "Belum punya akun?", label: "Daftar", href: `/signup${redirectAfterLogin ? `?redirect_after_login=${encodeURIComponent(redirectAfterLogin)}` : ""}` }}
      >
        <Heading>Masuk</Heading>
        <Subtitle>
          Lihat apa yang terjadi pada keterampilan perpajakan dan bisnis Anda. Akses materi, webinar, dan sertifikasi Brevet dalam satu akun.
        </Subtitle>

        {unconfirmed && (
          <InlineAlert
            variant="warning"
            title="Akun belum dikonfirmasi"
            role="alert"
            action={
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={resendingConfirm}
                style={{
                  background: "transparent",
                  border: "1px solid currentColor",
                  color: "inherit",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  fontWeight: 700,
                  fontSize: "12px",
                  cursor: resendingConfirm ? "wait" : "pointer",
                }}
              >
                {resendingConfirm ? "Mengirim…" : "Kirim ulang"}
              </button>
            }
          >
            Silakan cek inbox {unconfirmed.email} untuk tautan konfirmasi.
            {resendNotice && <div style={{ marginTop: 6 }}>{resendNotice}</div>}
          </InlineAlert>
        )}

        <GoogleSignInButton proceedTo={googleProceedTo} label="Masuk dengan Google" />

        <Divider>Atau</Divider>

        <form onSubmit={handleSubmit} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {errors.base && <BaseError role="alert">{errors.base}</BaseError>}

            <FormField id="login-email" label="Email" required error={errors.email}>
              <Control
                type="email"
                value={fields.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="mail@abc.com"
                autoComplete="email"
                required
              />
            </FormField>

            <PasswordField
              id="login-password"
              label="Password"
              required
              value={fields.password}
              onChange={(e) => setField("password", e.target.value)}
              error={errors.password}
              autoComplete="current-password"
              placeholder="Masukkan password Anda"
            />

            <Row>
              <RememberLabel>
                <input
                  type="checkbox"
                  checked={fields.remember}
                  onChange={(e) => setField("remember", e.target.checked)}
                />
                Ingat Saya
              </RememberLabel>
              <ForgotLink href="/authentications/passwords/new">Lupa Password?</ForgotLink>
            </Row>

            <SubmitButton submitting={submitting}>
              {submitting ? "Memproses…" : "Masuk"}
            </SubmitButton>

            <DaftarButton
              href={`/signup${redirectAfterLogin ? `?redirect_after_login=${encodeURIComponent(redirectAfterLogin)}` : ""}`}
            >
              Belum punya akun? Daftar
            </DaftarButton>
          </div>
        </form>
      </AuthLayout>
    </>
  );
}
